import type { Query } from "@tanstack/react-query"

/**
 * Decides which queries are dehydrated into the persisted cache
 * (localStorage via the async-storage persister).
 *
 * - The auth session is excluded: it is already durably stored by the
 *   platform auth storage (IndexedDB) and a duplicate copy here risks
 *   drift (see Provider.tsx).
 * - Queries tagged `meta: { persisted: false }` opt out. The online
 *   context fetches (PostgREST TEC/PEC polygons with full WKB geometry)
 *   opt out because a single large source can approach the ~5 MB
 *   localStorage quota — a QuotaExceededError drops the ENTIRE dehydrated
 *   cache, including the offline fallbacks that must survive reloads.
 *
 * Note: passing this predicate as `dehydrateOptions.shouldDehydrateQuery`
 * overrides the client default in query-core, so it must itself enforce the
 * `meta.persisted !== false` opt-out and the success-status condition that
 * the client's default dehydrate config declares.
 */
export function shouldPersistQuery(query: Query): boolean {
  const [scope, sub] = query.queryKey as [string, string?]
  if (scope === "auth" && sub === "session") return false
  return query.meta?.persisted !== false && query.state.status === "success"
}
